import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  PackageCheck, Search, X, ChevronDown, Trash2, Inbox,
  Loader2, Save, ShoppingCart, Receipt, ExternalLink,
  AlertTriangle, CheckCircle2, ChevronLeft
} from 'lucide-react';
import { SidePanel } from '../components/SidePanel';
import { SmartButton } from '../components/SmartButton';
import * as api from '../api';
import { toast } from 'sonner';
import { useSociete } from '../context/SocieteContext';

// ─── Constantes ───────────────────────────────────────────────────────────────

const STADES = [
  { key: 'brouillon',   label: 'Brouillon',   bg: '#F1F5F9', color: '#475569', border: '#CBD5E1', dot: '#94A3B8' },
  { key: 'en_cours',    label: 'Partiel',     bg: '#FFF7ED', color: '#B45309', border: '#FCD34D', dot: '#F59E0B' },
  { key: 'receptionne', label: 'Réceptionné', bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0', dot: '#22C55E' },
];

function getStade(key) { return STADES.find(s => s.key === key) || STADES[0]; }

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return d; }
}

function StageBadge({ stade }) {
  const s = getStade(stade);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
      {s.label}
    </span>
  );
}

// ─── ZoneCombobox ─────────────────────────────────────────────────────────────

function ZoneCombobox({ zones, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  // Ferme sur clic extérieur (hors trigger ET hors dropdown)
  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (dropdownRef.current?.contains(e.target)) return;
      setOpen(false);
      setQuery('');
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const handleOpen = (e) => {
    e.stopPropagation();
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
    }
    setOpen(v => !v);
    if (open) setQuery('');
  };

  const handleSelect = (zid) => {
    onChange(zid);
    setOpen(false);
    setQuery('');
  };

  const filtered = zones.filter(z => !query || z.nom.toLowerCase().includes(query.toLowerCase()));
  const selected = zones.find(z => z.zone_id === value);

  // Même pattern que Ventes.js : portal dans [role="dialog"] pour rester dans l'arbre Radix
  const portalTarget = document.querySelector('[role="dialog"]') || document.body;

  return (
    <div ref={triggerRef} style={{ width: 110, flexShrink: 0 }}>
      {/* Trigger */}
      <div onClick={handleOpen}
        style={{ display: 'flex', alignItems: 'center', gap: 4, border: `1px solid ${open ? 'var(--primary-solid)' : 'var(--border)'}`, borderRadius: 5, padding: '2px 6px', cursor: 'pointer', background: 'var(--surface)', height: 26, width: '100%', boxSizing: 'border-box' }}>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: selected ? 'var(--text)' : 'var(--text-placeholder)', fontSize: 11 }}>
          {selected ? selected.nom : '— Aucune —'}
        </span>
        <ChevronDown size={10} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
      </div>

      {/* Dropdown — portal dans [role="dialog"] comme Ventes.js */}
      {open && ReactDOM.createPortal(
        <div ref={dropdownRef}
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            width: Math.max(160, pos.width),
            zIndex: 9999,
            background: '#fff',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
            border: '1px solid var(--border)',
          }}
        >
          <div style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-light)' }}>
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              onMouseDown={e => e.stopPropagation()}
              placeholder="Chercher…"
              style={{ width: '100%', boxSizing: 'border-box', padding: '3px 6px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 11, outline: 'none', color: 'var(--text)' }}
            />
          </div>
          <div style={{ maxHeight: 160, overflowY: 'auto' }}>
            <div
              onMouseDown={e => { e.preventDefault(); e.stopPropagation(); handleSelect(''); }}
              style={{ padding: '6px 12px', fontSize: 11, cursor: 'pointer', color: 'var(--text-muted)', borderBottom: '1px solid var(--border-light)' }}>
              — Aucune zone —
            </div>
            {filtered.length === 0
              ? <div style={{ padding: '8px 12px', fontSize: 11, color: 'var(--text-muted)' }}>Aucune zone</div>
              : filtered.map(z => (
                <div key={z.zone_id}
                  onMouseDown={e => { e.preventDefault(); e.stopPropagation(); handleSelect(z.zone_id); }}
                  style={{ padding: '6px 12px', fontSize: 11, cursor: 'pointer', background: value === z.zone_id ? '#EFF6FF' : 'transparent', color: 'var(--text)', fontWeight: value === z.zone_id ? 600 : 400 }}>
                  {z.nom}
                </div>
              ))}
          </div>
        </div>,
        portalTarget
      )}
    </div>
  );
}

// ─── BRPanel — vue/édition uniquement (identique à BLDetailPanel) ─────────────

function BRPanel({ brId, onClose, onSaved, stocks }) {
  const { activeSociete } = useSociete();
  const isGlobalReadOnly = !activeSociete;
  const navigate = useNavigate();

  const [br, setBr]           = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [validating, setValidating] = useState(false);

  const [stockId, setStockId]     = useState('');
  const [stockNom, setStockNom]   = useState('');
  const [dateReception, setDateReception] = useState(''); // date_reception_entree_stock
  const [notes, setNotes]         = useState('');
  const [lignes, setLignes]       = useState([]);
  const [zones, setZones]         = useState([]);
  const [factureInfo, setFactureInfo] = useState(null);    // { numero, date_facture, montant_ht, devise, fichier_pdf_url, ... }
  const [brsTotaux, setBrsTotaux]     = useState(null);    // montant cumulé des BRs de la facture
  const [showPdf, setShowPdf]         = useState(true);

  const zonesCacheRef = useRef({});
  // Ref toujours synchrone avec le state lignes (pour les auto-saves onBlur)
  const lignesRef = useRef([]);

  const loadZones = useCallback(async (sid) => {
    if (!sid) { setZones([]); return; }
    if (zonesCacheRef.current[sid]) { setZones(zonesCacheRef.current[sid]); return; }
    try {
      const r = await api.getStock(sid);
      const z = r.data?.zones || [];
      zonesCacheRef.current[sid] = z;
      setZones(z);
    } catch { setZones([]); }
  }, []);

  const loadBR = useCallback(async () => {
    if (!brId) return;
    setLoading(true);
    try {
      const r = await api.getBonReception(brId);
      const d = r.data;
      setBr(d);
      // Utiliser le nouveau champ en priorité, fallback sur l'ancien
      setDateReception(d.date_reception_entree_stock?.slice(0, 10) || d.date_reception_prevue?.slice(0, 10) || '');
      setNotes(d.notes || '');
      setStockId(d.stock_id || '');
      setStockNom(d.stock_nom || '');
      setLignes((d.lignes || []).map(l => ({ ...l })));
      if (d.stock_id) await loadZones(d.stock_id);
      // Charger les infos facture liée
      if (d.facture_achat_id) {
        try {
          const [fRes, brsRes] = await Promise.all([
            api.getFacturesAchat(d.achat_id),
            api.getBrsDeFacture(d.facture_achat_id),
          ]);
          const fList = fRes.data || [];
          const fFound = fList.find(f => f.facture_id === d.facture_achat_id);
          setFactureInfo(fFound || null);
          const cumul = (brsRes.data || []).reduce((s, b) => s + (b.montant_br_devise || 0), 0);
          setBrsTotaux(cumul);
        } catch { /* silent */ }
      } else {
        setFactureInfo(null);
        setBrsTotaux(null);
      }
    } catch { toast.error('Erreur chargement BR'); }
    finally { setLoading(false); }
  }, [brId, loadZones]);

  useEffect(() => { loadBR(); }, [loadBR]);

  // Resynchronise si brId change (navigation entre BRs)
  useEffect(() => { setBr(null); setLoading(true); loadBR(); }, [brId]); // eslint-disable-line

  const handleStockChange = async (sid) => {
    const s = stocks.find(x => x.stock_id === sid);
    setStockId(sid);
    setStockNom(s?.nom || '');
    setLignes(prev => prev.map(l => ({ ...l, zone_id: '' })));
    await loadZones(sid);
  };

  const readOnly = br?.stade === 'receptionne' || isGlobalReadOnly;
  // Maintenir lignesRef synchronisé avec le state (pour handleAutoSaveQty)
  lignesRef.current = lignes;

  // ─── Calculs barre de progression ─────────────────────────────────────────
  const totalCmd       = lignes.reduce((s, l) => s + (l.quantite_commandee || 0), 0);
  const totalARecevoir = lignes
    .filter(l => l.article_type !== 'prestation')
    .reduce((s, l) => s + (l.quantite_a_recevoir || 0), 0);
  const pct = totalCmd > 0 ? Math.min(100, Math.round((totalARecevoir / totalCmd) * 100)) : 0;

  // ─── Actions ──────────────────────────────────────────────────────────────
  const buildLignesPayload = () => lignes.map((l, i) => ({
    achat_ligne_id: l.achat_ligne_id || '',
    article_id: l.article_id || '',
    article_ref: l.article_ref || '',
    article_designation: l.article_designation || '',
    quantite_commandee: l.quantite_commandee || 0,
    quantite_restante: Math.max(0, (l.quantite_commandee || 0) - (l.quantite_a_recevoir || 0)),
    quantite_a_recevoir: l.quantite_a_recevoir || 0,
    prix_unitaire_ht: l.prix_unitaire_ht || 0,
    taux_tva: l.taux_tva || 20,
    sequence: i,
    zone_id: l.zone_id || null,
    article_type: l.article_type || null,
  }));

  const handleSave = async () => {
    if (!br || saving) return;
    setSaving(true);
    try {
      const r = await api.updateBonReception(br.br_id, {
        stock_id: stockId || null,
        stock_nom: stockNom || null,
        notes: notes || null,
        date_reception_entree_stock: dateReception || null,
        lignes: buildLignesPayload(),
      });
      toast.success('Bon de réception mis à jour');
      onSaved?.(r.data);
      await loadBR();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally { setSaving(false); }
  };

  // Supprime une ligne du BR (toutes les lignes en mode édition)
  const handleDeleteLigne = async (idx) => {
    if (!br || readOnly) return;
    const next = lignes.filter((_, i) => i !== idx);
    setLignes(next);
    lignesRef.current = next;
    // Auto-save silencieux : persiste la suppression + nettoie les instances orphelines
    try {
      await api.updateBonReception(br.br_id, {
        stock_id: stockId || null,
        stock_nom: stockNom || null,
        notes: notes || null,
        date_reception_entree_stock: dateReception || null,
        lignes: next.map((l, i) => ({
          achat_ligne_id: l.achat_ligne_id || '',
          article_id: l.article_id || '',
          article_ref: l.article_ref || '',
          article_designation: l.article_designation || '',
          quantite_commandee: l.quantite_commandee || 0,
          quantite_restante: Math.max(0, (l.quantite_commandee || 0) - (l.quantite_a_recevoir || 0)),
          quantite_a_recevoir: l.quantite_a_recevoir || 0,
          prix_unitaire_ht: l.prix_unitaire_ht || 0,
          taux_tva: l.taux_tva || 20,
          sequence: i,
          zone_id: l.zone_id || null,
          article_type: l.article_type || null,
        })),
      });
      await loadBR();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur suppression ligne');
      await loadBR(); // recharger l'état cohérent en cas d'erreur
    }
  };

  // Auto-sauvegarde silencieuse à la perte du focus sur la quantité d'un matériel
  // → ajuste br_lignes_materiels côté backend + recharge les champs N° de série
  const handleAutoSaveQty = async () => {
    if (!br || br.stade === 'receptionne') return;
    try {
      await api.updateBonReception(br.br_id, {
        stock_id: stockId || null,
        stock_nom: stockNom || null,
        notes: notes || null,
        date_reception_entree_stock: dateReception || null,
        lignes: lignesRef.current.map((l, i) => ({
          achat_ligne_id: l.achat_ligne_id || '',
          article_id: l.article_id || '',
          article_ref: l.article_ref || '',
          article_designation: l.article_designation || '',
          quantite_commandee: l.quantite_commandee || 0,
          quantite_restante: Math.max(0, (l.quantite_commandee || 0) - (l.quantite_a_recevoir || 0)),
          quantite_a_recevoir: l.quantite_a_recevoir || 0,
          prix_unitaire_ht: l.prix_unitaire_ht || 0,
          taux_tva: l.taux_tva || 20,
          sequence: i,
          zone_id: l.zone_id || null,
          article_type: l.article_type || null,
        })),
      });
      await loadBR(); // recharge les instances br_lignes_materiels
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur mise à jour quantité');
    }
  };

  const handleValider = async () => {
    if (!br || validating) return;
    if (!stockId) return toast.error('Sélectionnez un dépôt de destination');
    setValidating(true);
    try {
      await api.updateBonReception(br.br_id, {
        stock_id: stockId || null,
        stock_nom: stockNom || null,
        notes: notes || null,
        date_reception_entree_stock: dateReception || null,
        lignes: buildLignesPayload(),
      });
      const r = await api.validerReception(br.br_id);
      toast.success('Réception validée — stock mis à jour');
      onSaved?.(r.data);
      await loadBR();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la validation');
    } finally { setValidating(false); }
  };

  const handleDelete = async () => {
    if (!br || br.stade !== 'brouillon') return;
    if (!window.confirm('Supprimer ce bon de réception ?')) return;
    try {
      await api.deleteBonReception(br.br_id);
      toast.success('Bon de réception supprimé');
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, padding: '80px 0' }}>
      <Loader2 size={22} style={{ color: 'var(--primary-solid)' }} className="animate-spin" />
    </div>
  );

  if (!br) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, padding: '80px 0' }}>
      <p style={{ color: 'var(--text-muted)' }}>Bon de réception introuvable</p>
    </div>
  );

  return (
    <>
    {/* ── Pane PDF (portail fixed à gauche du panel BR) ── */}
    {factureInfo?.fichier_pdf_url && showPdf && ReactDOM.createPortal(
      <div style={{
        position: 'fixed', top: 0, bottom: 0, right: '50vw', width: 'min(46vw, 720px)',
        background: '#fff', borderLeft: '1px solid #E2E8F0', borderRight: '1px solid #E2E8F0',
        display: 'flex', flexDirection: 'column', zIndex: 49, boxShadow: '-4px 0 20px rgba(0,0,0,0.08)',
      }}>
        {/* Header pane PDF */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, background: '#FAFAF8' }}>
          <Receipt size={14} style={{ color: 'var(--primary-solid)', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)' }}>{factureInfo.odoo_invoice_name || factureInfo.facture_id}</span>
            {factureInfo.numero_fournisseur && <span style={{ fontSize: 12, color: '#6B7280', marginLeft: 8 }}>· {factureInfo.numero_fournisseur}</span>}
            {factureInfo.date_facture && <span style={{ fontSize: 12, color: '#6B7280', marginLeft: 8 }}>— {factureInfo.date_facture}</span>}
          </div>
          {/* Contrôle montant */}
          {factureInfo.montant_ht != null && brsTotaux != null && (() => {
            const facMt  = factureInfo.montant_ht || 0;
            const diff   = Math.abs(brsTotaux - facMt);
            const ok     = diff < 0.01;
            return (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                background: ok ? '#D1FAE5' : '#FEF3C7',
                color: ok ? '#065F46' : '#92400E',
                border: `1px solid ${ok ? '#A7F3D0' : '#FDE68A'}`,
              }}>
                {ok ? <CheckCircle2 size={11} /> : <AlertTriangle size={11} />}
                BR&nbsp;:&nbsp;{brsTotaux.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}&nbsp;{factureInfo.devise || 'EUR'}
                &nbsp;/&nbsp;Facture&nbsp;:&nbsp;{facMt.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}&nbsp;{factureInfo.devise || 'EUR'}
              </div>
            );
          })()}
          <a href={factureInfo.fichier_pdf_url} target="_blank" rel="noreferrer"
            style={{ color: '#6B7280', flexShrink: 0, display: 'flex', alignItems: 'center' }} title="Ouvrir dans un nouvel onglet">
            <ExternalLink size={13} />
          </a>
          <button onClick={() => setShowPdf(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: 2, flexShrink: 0 }}>
            <ChevronLeft size={16} />
          </button>
        </div>
        <iframe src={factureInfo.fichier_pdf_url} title="Facture PDF"
          style={{ flex: 1, border: 'none', width: '100%' }} />
      </div>,
      document.body
    )}

    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* ── Header ── */}
      <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{br.numero}</h2>
              <StageBadge stade={br.stade} />
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              <strong style={{ color: 'var(--text)' }}>{br.fournisseur_nom || '—'}</strong>
              {br.achat_numero && (
                <>
                  {' · BC '}
                  <span onClick={() => navigate(`/achats?open_achat=${br.achat_id}`)}
                    style={{ color: 'var(--primary-solid)', cursor: 'pointer', textDecoration: 'underline' }}>
                    {br.achat_numero}
                  </span>
                </>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {br.achat_id && (
              <SmartButton
                icon={<ShoppingCart size={13} />}
                label="Commande d'achat"
                count={null}
                color="blue"
                onClick={() => { navigate(`/achats?open_achat=${br.achat_id}`); onClose(); }}
              />
            )}
            {factureInfo?.fichier_pdf_url && !showPdf && (
              <button onClick={() => setShowPdf(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 8, background: '#FEF3C7', border: '1px solid #FDE68A', color: '#92400E', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                <Receipt size={12} /> Voir PDF
              </button>
            )}
            <button onClick={onClose} data-testid="close-br-panel"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: 4, flexShrink: 0 }}>
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Barre de progression globale */}
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12 }}>
            <span style={{ color: 'var(--text-muted)' }}>Avancement</span>
            <span style={{ fontWeight: 700, color: pct >= 100 ? '#15803D' : 'var(--primary-solid)' }}>{pct}%</span>
          </div>
          <div style={{ height: 7, background: '#E2E8F0', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: pct >= 100 ? '#22C55E' : 'var(--primary-solid)', transition: 'width .4s' }} />
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px' }}>

        {/* Dépôt + date */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase' }}>
              Dépôt de destination{!readOnly && <span style={{ color: '#EF4444' }}> *</span>}
            </label>
            {readOnly ? (
              <div style={{ padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, background: '#F8FAFC', color: 'var(--text)' }}>
                {br.stock_nom || '—'}
              </div>
            ) : (
              <select value={stockId} onChange={e => handleStockChange(e.target.value)}
                data-testid="br-panel-stock-select"
                style={{ width: '100%', padding: '6px 10px', border: `1px solid ${stockId ? 'var(--border)' : '#FCA5A5'}`, borderRadius: 6, fontSize: 13, background: '#fff', color: 'var(--text)' }}>
                <option value="">— Sélectionner —</option>
                {stocks.map(s => <option key={s.stock_id} value={s.stock_id}>{s.nom}{s.agence_nom ? ` — ${s.agence_nom}` : ''}</option>)}
              </select>
            )}
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase' }}>
              Date de réception — Entrée en stock
            </label>
            <input type="date" value={dateReception} onChange={e => setDateReception(e.target.value)}
              disabled={readOnly}
              data-testid="br-panel-date-reception"
              style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, background: readOnly ? '#F8FAFC' : '#fff' }} />
          </div>
        </div>

        {/* Notes */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase' }}>Notes</label>
          <input type="text" value={notes} onChange={e => setNotes(e.target.value)} disabled={readOnly}
            placeholder="Observations, références transporteur…"
            style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, background: readOnly ? '#F8FAFC' : '#fff' }} />
        </div>

        {/* Tableau lignes */}
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'visible' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#FAFAFA' }}>
              <tr>
                {['Article', 'Qté cmd', 'À recevoir', 'Restant', 'Zone de rangement', '%', ''].map((h, i) => (
                  <th key={i} style={{
                    padding: '8px 10px',
                    textAlign: i === 0 || i === 4 ? 'left' : 'right',
                    fontSize: 11, color: 'var(--text-muted)', fontWeight: 600,
                    textTransform: 'uppercase', whiteSpace: 'nowrap',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lignes.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Aucune ligne</td></tr>
              ) : lignes.map((l, idx) => {
                const qte          = l.quantite_commandee || 0;
                const aRecevoir    = l.quantite_a_recevoir ?? 0;
                const restant      = Math.max(0, qte - aRecevoir);
                const pctL         = qte > 0 ? Math.min(100, Math.round((aRecevoir / qte) * 100)) : 0;
                const isMateriel   = l.article_type === 'materiel';
                const isPrestation = l.article_type === 'prestation';
                // Masquer les lignes à 0 en lecture seule (BR réceptionné)
                if (readOnly && aRecevoir === 0) return null;
                return (
                  <React.Fragment key={l.br_ligne_id || idx}>
                  <tr style={{ borderTop: '1px solid var(--border-light)', opacity: isPrestation ? 0.6 : 1, background: isPrestation ? '#FAFAF8' : 'transparent' }}>
                    <td style={{ padding: '8px 10px' }}>
                      {l.article_ref && !l.article_ref.startsWith('ART-') && (
                        <span style={{ fontFamily: 'monospace', color: '#94A3B8', marginRight: 6, fontSize: 11 }}>{l.article_ref}</span>
                      )}
                      <span style={{ color: 'var(--text)' }}>{l.article_designation || '—'}</span>
                      {isMateriel   && <span style={{ marginLeft: 6, fontSize: 10, background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE', borderRadius: 4, padding: '1px 5px', fontWeight: 600 }}>Matériel</span>}
                      {isPrestation && <span style={{ marginLeft: 6, fontSize: 10, background: '#F3F4F6', color: '#6B7280', border: '1px solid #E5E7EB', borderRadius: 4, padding: '1px 5px', fontWeight: 600 }}>Prestation</span>}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--text-secondary)' }}>{qte}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                      {(readOnly || isPrestation) ? (
                        <span style={{ fontWeight: 600, color: isPrestation ? '#94A3B8' : pctL >= 100 ? '#15803D' : 'var(--primary-solid)' }}>
                          {isPrestation ? '—' : aRecevoir}
                        </span>
                      ) : (
                        <input
                          data-testid={`qte-a-recevoir-${l.br_ligne_id}`}
                          type="number" min={0} max={qte} step={isMateriel ? 1 : 0.01} value={aRecevoir}
                          onChange={e => {
                            const next = [...lignes];
                            next[idx] = { ...l, quantite_a_recevoir: Math.min(qte, Math.max(0, Number(e.target.value))) };
                            setLignes(next);
                          }}
                          onBlur={isMateriel ? handleAutoSaveQty : undefined}
                          style={{ width: 64, padding: '3px 6px', border: '1px solid var(--border)', borderRadius: 4, textAlign: 'right', fontSize: 13 }}
                        />
                      )}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: isPrestation ? '#94A3B8' : restant > 0 ? '#C2410C' : '#15803D' }}>
                      {isPrestation ? '—' : restant}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      {isPrestation ? (
                        <span style={{ color: '#94A3B8', fontSize: 11, fontStyle: 'italic' }}>Non applicable</span>
                      ) : (
                        <span style={{ fontSize: 11 }}>
                          {l.zone_nom ? (
                            <>
                              <span style={{ color: 'var(--text-secondary)' }}>{l.zone_nom}</span>
                              {l.is_zone_defaut && (
                                <span style={{ marginLeft: 4, fontSize: 10, background: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A', borderRadius: 3, padding: '0 4px' }}>transit</span>
                              )}
                            </>
                          ) : (
                            <span style={{ color: '#94A3B8', fontStyle: 'italic' }}>Non affectée</span>
                          )}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '8px 10px', minWidth: 80 }}>
                      {isPrestation ? (
                        <span style={{ fontSize: 10, color: '#94A3B8', fontStyle: 'italic' }}>Informatif</span>
                      ) : (
                        <>
                        <div style={{ height: 5, background: '#E2E8F0', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pctL}%`, background: pctL >= 100 ? '#22C55E' : '#3B82F6', transition: 'width .3s' }} />
                        </div>
                        <div style={{ fontSize: 10, color: '#94A3B8', textAlign: 'right', marginTop: 1 }}>{pctL}%</div>
                        </>
                      )}
                    </td>
                    <td style={{ padding: '4px 6px', textAlign: 'center', width: 28 }}>
                      {!readOnly && (
                        <button
                          data-testid={`delete-ligne-${l.br_ligne_id}`}
                          onClick={() => handleDeleteLigne(idx)}
                          title="Retirer cette ligne du bon de réception"
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: '#CBD5E1', padding: 2, borderRadius: 4, lineHeight: 1,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                          onMouseEnter={e => e.currentTarget.style.color = '#EF4444'}
                          onMouseLeave={e => e.currentTarget.style.color = '#CBD5E1'}
                        >
                          <X size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                  {/* ── Sous-lignes instances matériel ── */}
                  {isMateriel && l.materiel_instances && l.materiel_instances.length > 0 && (
                    <tr style={{ background: '#F8FAFF' }}>
                      <td colSpan={6} style={{ padding: '0 10px 10px 32px' }}>
                        <div style={{ fontSize: 11, color: '#64748B', fontWeight: 600, marginBottom: 6, paddingTop: 8 }}>
                          Numéros de série ({l.materiel_instances.filter(i => i.numero_serie).length}/{l.materiel_instances.length} renseignés)
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {l.materiel_instances.map((inst, iIdx) => (
                            <div key={inst.instance_id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: 11, color: '#94A3B8', minWidth: 18, textAlign: 'right' }}>{iIdx + 1}.</span>
                              <input
                                data-testid={`serie-${inst.instance_id}`}
                                placeholder="Numéro de série *"
                                defaultValue={inst.numero_serie || ''}
                                disabled={inst.statut === 'receptionne' || readOnly}
                                onBlur={async e => {
                                  const val = e.target.value.trim();
                                  if (val === (inst.numero_serie || '')) return;
                                  try {
                                    const res = await api.updateBrMaterielInstance(brId, inst.instance_id, { numero_serie: val || null });
                                    setLignes(prev => prev.map((ln, li) => li !== idx ? ln : {
                                      ...ln,
                                      materiel_instances: ln.materiel_instances.map((m, mi) => mi === iIdx ? res.data : m)
                                    }));
                                  } catch { toast.error('Erreur sauvegarde numéro de série'); }
                                }}
                                style={{ flex: 2, padding: '3px 8px', border: `1px solid ${!inst.numero_serie && inst.statut !== 'receptionne' ? '#FCA5A5' : 'var(--border)'}`, borderRadius: 4, fontSize: 12, background: inst.statut === 'receptionne' ? '#F0FDF4' : 'white' }}
                              />
                              <input
                                data-testid={`prix-${inst.instance_id}`}
                                type="number" min={0} step={0.01}
                                placeholder="Prix achat HT"
                                defaultValue={inst.prix_achat ?? ''}
                                disabled={inst.statut === 'receptionne' || readOnly}
                                onBlur={async e => {
                                  const val = e.target.value === '' ? null : parseFloat(e.target.value);
                                  if (val === (inst.prix_achat ?? null)) return;
                                  try {
                                    const res = await api.updateBrMaterielInstance(brId, inst.instance_id, { prix_achat: val });
                                    setLignes(prev => prev.map((ln, li) => li !== idx ? ln : {
                                      ...ln,
                                      materiel_instances: ln.materiel_instances.map((m, mi) => mi === iIdx ? res.data : m)
                                    }));
                                  } catch { toast.error('Erreur sauvegarde prix achat'); }
                                }}
                                style={{ width: 110, padding: '3px 8px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 12, textAlign: 'right', background: inst.statut === 'receptionne' ? '#F0FDF4' : 'white' }}
                              />
                              <input
                                data-testid={`annee-${inst.instance_id}`}
                                type="number" min={1900} max={2100} step={1}
                                placeholder="Année fab."
                                defaultValue={inst.annee ?? ''}
                                disabled={inst.statut === 'receptionne' || readOnly}
                                onBlur={async e => {
                                  const val = e.target.value === '' ? null : parseInt(e.target.value, 10);
                                  if (val === (inst.annee ?? null)) return;
                                  try {
                                    const res = await api.updateBrMaterielInstance(brId, inst.instance_id, { annee: val });
                                    setLignes(prev => prev.map((ln, li) => li !== idx ? ln : {
                                      ...ln,
                                      materiel_instances: ln.materiel_instances.map((m, mi) => mi === iIdx ? res.data : m)
                                    }));
                                  } catch { toast.error('Erreur sauvegarde année'); }
                                }}
                                style={{ width: 80, padding: '3px 8px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 12, textAlign: 'right', background: inst.statut === 'receptionne' ? '#F0FDF4' : 'white' }}
                              />
                              <span style={{ fontSize: 10, fontWeight: 600, color: inst.statut === 'receptionne' ? '#15803D' : '#94A3B8', minWidth: 72 }}>
                                {inst.statut === 'receptionne' ? '✓ Réceptionné' : 'En attente'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Récap 3 blocs — identique BLDetailPanel */}
        {totalCmd > 0 && (
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            {[
              { label: 'Commandé',   value: totalCmd,                               bg: '#F8FAFC', border: '#E2E8F0', color: 'var(--text)' },
              { label: 'À recevoir', value: totalARecevoir,                          bg: '#F0FDF4', border: '#BBF7D0', color: '#15803D' },
              { label: 'Restant',    value: Math.max(0, totalCmd - totalARecevoir),  bg: '#FFF7ED', border: '#FED7AA', color: '#C2410C' },
            ].map(({ label, value, bg, border, color }) => (
              <div key={label} style={{ flex: 1, background: bg, border: `1px solid ${border}`, borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ fontSize: 11, color, fontWeight: 600 }}>{label}</div>
                <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Footer ── */}
      <div style={{ borderTop: '1px solid var(--border)', padding: '12px 24px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div>
            {br.stade === 'brouillon' && !isGlobalReadOnly && (
              <button onClick={handleDelete}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all"
                style={{ borderColor: '#FECACA', color: '#DC2626', background: '#fff' }}>
                <Trash2 size={13} />
                Supprimer
              </button>
            )}
          </div>
          {!readOnly && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleSave} disabled={saving}
                data-testid="br-panel-save-btn"
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all hover:bg-gray-50"
                style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)', background: '#fff' }}>
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                {saving ? '...' : 'Enregistrer'}
              </button>
              <button onClick={handleValider} disabled={validating}
                data-testid="btn-valider-reception"
                className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold text-white transition-all"
                style={{ background: validating ? '#94A3B8' : '#15803D', cursor: validating ? 'not-allowed' : 'pointer' }}>
                {validating ? <Loader2 size={13} className="animate-spin" /> : <PackageCheck size={13} />}
                {validating ? 'Validation...' : 'Valider la réception'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}


// ─── Page principale ──────────────────────────────────────────────────────────

export default function BonsReception() {
  const { activeSociete, activeAgences } = useSociete();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [brs, setBrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStade, setFilterStade] = useState([]);
  const [stocks, setStocks] = useState([]);

  // panel : { br_id } ou { achat_id }
  const [panel, setPanel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [brsRes, stocksRes] = await Promise.all([
        api.getBonsReception({}),
        api.getStocks({ limit: 200 }),
      ]);
      setBrs(brsRes.data || []);
      setStocks(stocksRes.data?.items || stocksRes.data || []);
    } catch { toast.error('Erreur chargement bons de réception'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Ouvrir via URL param
  useEffect(() => {
    const openBr  = searchParams.get('open_br') || searchParams.get('br_id');
    if (openBr && !panel) {
      setPanel({ br_id: openBr });
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]); // eslint-disable-line

  // Filtre agence actif
  const activeAgenceIds = new Set((activeAgences || []).map(a => a.agence_id));

  const filtered = brs.filter(br => {
    if (activeAgenceIds.size > 0 && br.agence_id && !activeAgenceIds.has(br.agence_id)) return false;
    if (filterStade.length && !filterStade.includes(br.stade)) return false;
    if (search) {
      const q = search.toLowerCase();
      return br.numero?.toLowerCase().includes(q)
        || br.fournisseur_nom?.toLowerCase().includes(q)
        || br.achat_numero?.toLowerCase().includes(q)
        || br.stock_nom?.toLowerCase().includes(q);
    }
    return true;
  });

  // Stocks filtrés par agence active (pour le sélecteur dépôt dans BRPanel)
  const filteredStocks = activeAgenceIds.size > 0
    ? stocks.filter(s => !s.agence_id || activeAgenceIds.has(s.agence_id))
    : stocks;

  // Stats basées sur les BRs de l'agence active uniquement
  const brsAgence = activeAgenceIds.size > 0
    ? brs.filter(b => !b.agence_id || activeAgenceIds.has(b.agence_id))
    : brs;
  const stats = STADES.map(s => ({ ...s, count: brsAgence.filter(b => b.stade === s.key).length }));

  const toggleStade = (k) => setFilterStade(prev =>
    prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]
  );

  return (
    <div className="flex h-full" style={{  }}>
      {/* Liste */}
      <div className={`flex flex-col flex-1 min-w-0 transition-all ${panel ? 'hidden sm:flex' : ''}`}>
        {/* Toolbar */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0"
          style={{ borderColor: 'var(--border-light)', background: 'var(--bg-page)' }}>
          <div>
            <h1 className="text-lg font-bold" style={{ color: 'var(--text)',  }}>Bons de réception</h1>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{brs.length} bon{brs.length !== 1 ? 's' : ''} au total</p>
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher..."
              data-testid="br-search-input"
              className="pl-8 pr-3 py-2 border rounded-xl text-sm w-56"
              style={{ borderColor: 'var(--border)', color: 'var(--text)', background: 'var(--card)' }} />
          </div>
        </div>

        {/* Stats pills */}
        <div className="flex items-center gap-2 px-6 py-3 border-b flex-wrap"
          style={{ borderColor: 'var(--border-light)', background: 'var(--bg-subtle)' }}>
          <button onClick={() => setFilterStade([])}
            className="px-3 py-1 rounded-full text-xs font-semibold transition-all border"
            style={{
              background: filterStade.length === 0 ? 'var(--primary-solid)' : 'white',
              color: filterStade.length === 0 ? 'white' : 'var(--text-muted)',
              borderColor: filterStade.length === 0 ? 'var(--primary-solid)' : 'var(--border)',
            }}>
            Tous ({brs.length})
          </button>
          {stats.map(s => (
            <button key={s.key} onClick={() => toggleStade(s.key)}
              data-testid={`filter-stade-${s.key}`}
              className="px-3 py-1 rounded-full text-xs font-semibold transition-all border flex items-center gap-1.5"
              style={{
                background: filterStade.includes(s.key) ? s.bg : 'white',
                color: filterStade.includes(s.key) ? s.color : 'var(--text-muted)',
                borderColor: filterStade.includes(s.key) ? s.border : 'var(--border)',
              }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
              {s.label} ({s.count})
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin w-7 h-7 border-2 rounded-full"
                style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <Inbox size={36} className="mx-auto mb-3 opacity-15" style={{ color: 'var(--text-disabled)' }} />
              <p className="text-sm font-semibold" style={{ color: 'var(--text-muted)' }}>Aucun bon de réception</p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-placeholder)' }}>
                {search ? 'Essayez un autre terme de recherche' : "Créez un bon depuis une commande d'achat"}
              </p>
            </div>
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr style={{ background: 'var(--bg-subtle)', borderBottom: '2px solid var(--border)' }}>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Numéro</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Fournisseur</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Commande</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Dépôt</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide w-28" style={{ color: 'var(--text-muted)' }}>Progression</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Statut</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(br => {
                  const pct = br.total_a_recevoir > 0
                    ? Math.round(((br.total_recu || 0) / br.total_a_recevoir) * 100)
                    : 0;
                  const isOpen = panel?.br_id === br.br_id;
                  return (
                    <tr key={br.br_id}
                      onClick={() => setPanel({ br_id: br.br_id })}
                      data-testid={`br-row-${br.br_id}`}
                      className="cursor-pointer hover:bg-gray-50 transition-colors"
                      style={{ borderBottom: '1px solid var(--border-light)', background: isOpen ? 'var(--primary-50)' : undefined }}>
                      <td className="px-4 py-3 font-semibold" style={{ color: 'var(--primary-solid)' }}>{br.numero}</td>
                      <td className="px-4 py-3" style={{ color: 'var(--text)' }}>{br.fournisseur_nom || '—'}</td>
                      <td className="px-4 py-3" style={{ color: 'var(--text-muted)' }}>{br.achat_numero || '—'}</td>
                      <td className="px-4 py-3" style={{ color: 'var(--text-muted)' }}>{br.stock_nom || '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 justify-center">
                          <div style={{ width: 60, height: 4, background: '#E2E8F0', borderRadius: 2, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: pct >= 100 ? '#22C55E' : '#3B82F6' }} />
                          </div>
                          <span className="text-xs tabular-nums" style={{ color: 'var(--text-muted)' }}>{pct}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3"><StageBadge stade={br.stade} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Panel latéral — identique BonsLivraisonVentes */}
      {panel && (
        <SidePanel
          open={!!panel}
          onOpenChange={(v) => { if (!v) setPanel(null); }}
          title={brs.find(b => b.br_id === panel.br_id)?.numero || 'Bon de réception'}
          copyValue={brs.find(b => b.br_id === panel.br_id)?.numero}
          subtitle={(() => { const f = brs.find(b => b.br_id === panel.br_id); return f ? `${f.fournisseur_nom || ''}${f.achat_numero ? ` · ${f.achat_numero}` : ''}` : ''; })()}
          size="lg">
          <BRPanel
            brId={panel.br_id || null}
            stocks={filteredStocks}
            onClose={() => setPanel(null)}
            onSaved={(br) => {
              load();
              if (br?.br_id) setPanel({ br_id: br.br_id });
              else setPanel(null);
            }}
          />
        </SidePanel>
      )}
    </div>
  );
}
